<template>
  <div class="reporting">
    <div class="reporting-top-bar">
      <div class="reporting-date-range">
        <span class="material-icons">calendar_today</span>
        <span>{{ $t(`reporting.calendar.dateRange`) }}:</span>
        <DateRangePicker
          v-model="dateRange"
          :maxDate="new Date()"
          @update="loadData"
          opens="right"
          :localeData="localeData"
          :ranges="ranges"
        />
      </div>
      <div v-if="dashboard.routes_data.length > 0" class="report-export">
        <button @click="createBranchReportingExport" class="button button-green button-small button-round">
          {{ $t('reporting.branch.createBranchReportingExport') }}
        </button>
      </div>
    </div>
    <LoadingSpinner v-if="loading" />
    <div v-else class="reporting-body">
      <template v-if="dashboard.routes_data.length > 0">
        <div class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('reporting.generalSummary') }}</h3>
          </header>
          <div class="content-body">
            <div class="reporting-numbers">
              <div class="reporting-card">
                <div class="reporting-card-content">
                  <div class="reporting-card-header">
                    <span class="material-icons">map</span>
                    <span>{{ $t(`reporting.summary.routes`) }}</span>
                  </div>
                  <div class="reporting-card-body">{{ dashboard.main_values.routes_count }}</div>
                </div>
              </div>
              <div class="reporting-card">
                <div class="reporting-card-content">
                  <div class="reporting-card-header">
                    <span class="material-icons">share_location</span>
                    <span>{{ $t(`reporting.summary.groups`) }}</span>
                  </div>
                  <div class="reporting-card-body">{{ dashboard.main_values.average_stops_count.toFixed(1) }}</div>
                </div>
              </div>
              <div class="reporting-card">
                <div class="reporting-card-content">
                  <div class="reporting-card-header">
                    <span class="material-icons">delete</span>
                    <span>{{ $t(`reporting.summary.containers`) }}</span>
                  </div>
                  <div class="reporting-card-body">{{ dashboard.main_values.average_containers_count.toFixed(1) }}</div>
                </div>
              </div>
              <div class="reporting-card">
                <div class="reporting-card-content">
                  <div class="reporting-card-header">
                    <span class="material-icons">restore_from_trash</span>
                    <span>{{ $t(`reporting.summary.fillLevel`) }}</span>
                  </div>
                  <div class="reporting-card-body">
                    {{ (dashboard.main_values.average_fill_level * 100).toFixed(1) }} %
                  </div>
                </div>
              </div>
              <div class="reporting-card">
                <div class="reporting-card-content">
                  <div class="reporting-card-header">
                    <span class="material-icons">delete_forever</span>
                    <span
                      >{{ $t(`reporting.summary.overflows.firstLine`) }}<br />{{
                        $t(`reporting.summary.overflows.secondLine`)
                      }}</span
                    >
                  </div>
                  <div class="reporting-card-body">{{ dashboard.main_values.count_overflows }}</div>
                </div>
              </div>
            </div>
            <div v-if="showRoutesChart" class="reporting-chart">
              <span>{{ $t('reporting.averageFillLevelRoute') }}</span>
              <RoutesChart :routes="dashboard.routes_data" :dateRange="dateRange" />
            </div>
          </div>
        </div>
        <div class="content-wrap">
          <header class="content-header cf">
            <h3>{{ $t('reporting.projectSummary') }}</h3>
          </header>
          <div class="content-body">
            <Tabs :tabList="projects">
              <template v-for="(project, i) in projects" #[`tab-${i}`]>
                <div
                  :key="i"
                  v-if="!getRoutesDataForProject(project.name).length && !getGroupsDataForProject(project.name).length"
                  class="alert alert-info"
                >
                  {{ $t('reporting.noData') }}
                </div>
                <div v-else :key="project.name" class="wrapper">
                  <RoutesTable
                    v-if="getRoutesDataForProject(project.name).length > 0"
                    :routesData="getRoutesDataForProject(project.name)"
                  />
                  <ProjectFillLevel
                    v-if="getGroupsDataForProject(project.name).length > 0"
                    :projectData="getGroupsDataForProject(project.name)"
                  />
                </div>
              </template>
            </Tabs>
          </div>
        </div>
      </template>
      <div v-else class="alert alert-info">
        {{ $t('reporting.noData') }}
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import DateRangePicker from 'vue2-daterange-picker'

import { DATEPICKER_LOCALE_DATA, DATEPICKER_RANGES } from '@/views/config'
import RoutesChart from '@/views/reporting/RoutesChart.vue'

import LoadingSpinner from '@/components/LoadingSpinner/index.vue'
import ProjectFillLevel from '@/components/Reporting/ProjectFillLevel.vue'
import RoutesTable from '@/components/Reporting/RoutesTable.vue'
import Tabs from '@/components/Tabs'

import exportsApi from '@/api/exports'
import reportingApi from '@/api/reporting'

export default {
  name: 'ReportingBranch',
  inject: ['companyUser'],
  components: {
    DateRangePicker,
    LoadingSpinner,
    ProjectFillLevel,
    RoutesChart,
    RoutesTable,
    Tabs,
  },
  props: {
    projectUuids: { type: Array, required: false },
  },
  data: () => ({
    dateRange: { startDate: moment().subtract(1, 'months').toDate(), endDate: new Date() },
    dashboard: {
      main_values: {},
      routes_data: [],
      projects_data: [],
    },
    loading: true,
  }),
  computed: {
    localeData() {
      return DATEPICKER_LOCALE_DATA[this.companyUser.user.language]
    },
    ranges() {
      return DATEPICKER_RANGES[this.companyUser.user.language]
    },
    projects() {
      return this.dashboard.projects_data.map(project => {
        return {
          disabled: false,
          key: project.project_uuid,
          name: project.project_name,
        }
      })
    },
    showRoutesChart() {
      return this.dashboard.routes_data.filter(route => route.average_fill_level > 0).length > 0
    },
  },
  methods: {
    async loadData() {
      if (this.projectUuids == null) return

      this.loading = true
      this.dashboard = await reportingApi.getForBranch({
        company_uuid: this.companyUser.company_uuid,
        project_uuids: this.projectUuids,
        from: this.dateRange.startDate,
        to: this.dateRange.endDate,
      })
      this.loading = false
    },
    getRoutesDataForProject(projectName) {
      return this.dashboard.routes_data.filter(route => route.project_name === projectName)
    },
    getGroupsDataForProject(projectName) {
      const projectData = this.dashboard.projects_data.filter(project => project.project_name === projectName)
      return projectData[0].groups_data.filter(
        group => (group.average_fill_level < 0.5 || group.average_fill_level > 1.0) && group.data_point_count > 5,
      )
    },
    async createBranchReportingExport() {
      await exportsApi.create({
        type: 'branch_reporting_xlsx',
        company_uuid: this.companyUser.company_uuid,
        project_uuids: this.projectUuids,
        from: this.dateRange.startDate,
        to: this.dateRange.endDate,
      })
    },
  },
  watch: {
    projectUuids() {
      this.loadData()
    },
  },
  async mounted() {
    await this.loadData()
  },
}
</script>

<style lang="scss" scoped>
@import 'reporting.scss';
</style>
